import fetchWithAuthorization from './fetch-with-authorization'
import { END_POINTS } from './end-points'

// Partner Admin API calls

/**
 * Add multiple businesses with their locations.
 * Mirrors Fasten-Core BusinessController POST /api/business-admin/business
 * @param {object} businessDto - Payload matching BusinessDto
 * @returns {Promise<object>} { data, meta? }
 */
export const addBusiness = async businessDto => {
  const response = await fetchWithAuthorization({
    path: END_POINTS.BUSINESS_ADMIN.BUSINESS,
    method: 'POST',
    body: JSON.stringify(businessDto),
  })
  return response
}

/**
 * Get all businesses with their locations and reward classes.
 * @returns {Promise<object>} { businesses: BusinessResDto[] }
 */
export const getBusinesses = async () => {
  const response = await fetchWithAuthorization({
    path: END_POINTS.BUSINESS_ADMIN.BUSINESSES,
    method: 'GET',
  })
  return response
}

/**
 * Add a single business location to an existing business
 * Mirrors Fasten-Core BusinessController POST /api/business-admin/business/:businessId/business-location
 * @param {string} businessId
 * @param {object} locationDto - Payload matching AddBusinessLocationDto
 * @returns {Promise<object>} { data, meta? }
 */
export const addBusinessLocation = async (businessId, locationDto) => {
  const response = await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS}/${businessId}/business-location`,
    method: 'POST',
    body: JSON.stringify(locationDto),
  })
  return response
}

/**
 * Update a business by ID
 * @param {string} businessId
 * @param {object} updateData - Payload matching UpdateBusinessDto
 * @returns {Promise<object>} { eventId }
 */
export const updateBusiness = async (businessId, updateData) => {
  return await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS}/${businessId}`,
    method: 'PATCH',
    body: JSON.stringify(updateData),
  })
}

/**
 * Update a business location by ID
 * @param {string} businessLocationId
 * @param {object} updateData - Payload matching UpdateBusinessLocationDto
 * @returns {Promise<object>} { eventId }
 */
export const updateBusinessLocation = async (businessLocationId, updateData) => {
  return await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS_LOCATION}/${businessLocationId}`,
    method: 'PATCH',
    body: JSON.stringify(updateData),
  })
}

/**
 * Add a new reward class for a business location
 * Mirrors Fasten-Core BusinessController POST /api/business-admin/business-location/:businessLocationId/reward-class
 * @param {string} businessLocationId
 * @param {object} rewardClassDto - { rewardClass: string, startDate: string, endDate: string|null }
 * @returns {Promise<object>} { data }
 */
export const addBusinessLocationRewardClass = async (businessLocationId, rewardClassDto) => {
  return await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS_LOCATION}/${businessLocationId}/reward-class`,
    method: 'POST',
    body: JSON.stringify(rewardClassDto),
  })
}

/**
 * Update a reward class for a business location (by original startDate)
 * Mirrors Fasten-Core BusinessController PATCH /api/business-admin/business-location/:businessLocationId/reward-class/:startDate
 * @param {string} businessLocationId
 * @param {string} originalStartDate - yyyy-MM-dd of the reward class to update
 * @param {object} updateDto - { rewardClass: string, startDate: string, endDate: string|null }
 * @returns {Promise<object>} { data }
 */
export const updateBusinessLocationRewardClass = async (businessLocationId, originalStartDate, updateDto) => {
  return await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS_LOCATION}/${businessLocationId}/reward-class/${originalStartDate}`,
    method: 'PATCH',
    body: JSON.stringify(updateDto),
  })
}

/**
 * Delete a business location by ID (soft delete)
 * @param {string} businessLocationId
 * @returns {Promise<object>} { eventId }
 */
export const deleteBusinessLocation = async businessLocationId => {
  // TODO: For now, set location inactive prior to soft delete, since
  // Fasten-Core does not properly handle soft deleted business locations
  await updateBusinessLocation(businessLocationId, { isActive: false })
  return await fetchWithAuthorization({
    path: `${END_POINTS.BUSINESS_ADMIN.BUSINESS_LOCATION}/${businessLocationId}`,
    method: 'DELETE',
  })
}

/**
 * Get all reward classes
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 10)
 * @returns {Promise<object>} { data: RewardClassEntity[], meta: PaginationObject }
 */
export const getRewardClasses = async (page = 1, perPage = 100) => {
  const response = await fetchWithAuthorization({
    path: END_POINTS.REWARDS_CLASS.GET_CLASS(page, perPage),
    method: 'GET',
  })
  return response
}

export default {
  getBusinesses,
  addBusiness,
  addBusinessLocation,
  updateBusiness,
  updateBusinessLocation,
  addBusinessLocationRewardClass,
  updateBusinessLocationRewardClass,
  deleteBusinessLocation,
  getRewardClasses,
}
